import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirm_password: ''
  });

  const token = searchParams.get('token');

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error('Invalid reset link');
      return;
    }

    if (!formData.password) {
      toast.error('Please enter a new password');
      return;
    }

    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    // Client-side pre-check for a fast error message -- the backend enforces the
    // real rule (8+ chars, upper/lower/digit/special) regardless of this check,
    // same as registration (see RegisterPage.jsx).
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters, with uppercase, lowercase, a digit and a special character');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, formData.password);
      setSuccess(true);
    } catch (err) {
      console.error('Reset password error:', err);
      const detail = err.response?.data?.detail;
      // A 422 from the Pydantic password-strength validator (e.g. the common-password
      // blocklist, which the client-side check above can't replicate) comes back as
      // FastAPI's default array-of-errors shape, not a plain string -- pull the first
      // message out instead of rendering the array itself.
      const message = Array.isArray(detail)
        ? detail[0]?.msg?.replace(/^Value error,\s*/, '') || 'Failed to reset password'
        : detail || 'Failed to reset password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 grid-pattern" data-testid="reset-password-page">
        <Card className="w-full max-w-md border-2">
          <CardContent className="pt-8 text-center">
            <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight mb-2 text-destructive">Invalid Link</h2>
            <p className="text-muted-foreground mb-6">
              This password reset link is invalid or has expired.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full h-12 bg-chassis hover:bg-chassis/90 font-bold uppercase tracking-wider">
                Request New Link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 grid-pattern" data-testid="reset-password-page">
        <Card className="w-full max-w-md border-2">
          <CardContent className="pt-8 text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight mb-2">Password Reset!</h2>
            <p className="text-muted-foreground mb-6">
              Your password has been successfully reset.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-chassis hover:bg-chassis/90 font-bold uppercase tracking-wider"
              data-testid="goto-login-btn"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 grid-pattern" data-testid="reset-password-page">
      <Card className="w-full max-w-md border-2">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 bg-chassis rounded-sm flex items-center justify-center mb-4">
            <Lock className="h-6 w-6 text-white" />
          </div>
          <CardTitle className="font-chivo text-2xl uppercase tracking-tight">Reset Password</CardTitle>
          <CardDescription>Enter your new password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 8 characters"
                  value={formData.password}
                  onChange={handleChange}
                  className="h-12 border-2 pr-10"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                8+ characters, with uppercase, lowercase, a digit and a special character.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                name="confirm_password"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirm_password}
                onChange={handleChange}
                className="h-12 border-2"
                data-testid="reset-confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 bg-chassis hover:bg-chassis/90 font-bold uppercase tracking-wider"
              disabled={loading}
              data-testid="reset-submit-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
